/**
 * PipelineClient360 — the crew-facing client 360 + per-job workflow drill-down.
 *
 * Dylan 2026-07-22: "click a client → see EVERYTHING they have with us; click a
 * specific request/job → see the whole workflow built from it (request → job →
 * visits → invoice → communications → notes)."
 *
 * Two screens, both URL-driven (deep-linkable, back-button friendly):
 *   /pipeline?tab=clients&client=<gid>            → the 360
 *   /pipeline?tab=clients&client=<gid>&job=<n>    → workflow view on a job
 *   /pipeline?tab=clients&client=<gid>&request=<id> → workflow view on a request
 *
 * The 360 is ONE read (/api/pipeline action:client-360, cached tables only).
 * The workflow view reuses the existing flow spine: FlowStrip + /api/flow
 * (chain + jobDetail) + /api/takeoff jobMedia for notes/photos. This is the
 * crew-facing twin of the client hub — same data spine, admin audience — and
 * deliberately shares NO code or endpoints with /hub.
 */
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiFetch } from "../lib/apiFetch";
import FlowStrip from "./FlowStrip";
import MediaGallery from "./MediaGallery";

const C = {
  bg: "#0d0f12", surface: "#161a20", card: "#1c2028", card2: "#12151b",
  border: "#2a3140", lime: "#a5ce3a", limeDim: "rgba(165,206,58,.12)",
  text: "#e8eaf0", muted: "#8892a4", faint: "#5a6472",
  blue: "#3b82f6", amber: "#f59e0b", green: "#10b981", red: "#ef4444", purple: "#8b5cf6",
};

const money = (n) => (n == null || isNaN(+n) ? "—" : "$" + Math.round(+n).toLocaleString("en-CA"));
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" }) : "—");
const fmtDateShort = (s) => (s ? new Date(s).toLocaleDateString("en-CA", { month: "short", day: "numeric" }) : "—");
const fmtDateTime = (s) => (s ? new Date(s).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
const fmtTime = (s) => (s ? new Date(s).toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit" }) : "");
const titleCase = (s) => String(s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

async function callApi(url, payload) {
  try {
    const r = await apiFetch(url, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const j = await r.json().catch(() => ({ ok: false, error: `HTTP ${r.status}` }));
    if (!r.ok && j.ok === undefined) return { ok: false, error: `HTTP ${r.status}` };
    return j;
  } catch (e) {
    return { ok: false, error: e?.message || "Network error" };
  }
}

// ── Atoms (local, per house precedent — FlowStrip/JobDetail carry their own) ──
const S = {
  card: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, marginBottom: 14 },
  sectionH: { fontSize: 12, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 10 },
  row: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "9px 11px", background: C.card2, borderRadius: 8, marginBottom: 6 },
};

function Btn({ kind = "btn", children, ...p }) {
  const base = kind === "pri"
    ? { padding: "8px 15px", borderRadius: 8, border: "none", background: C.lime, color: "#12200a", fontWeight: 800 }
    : kind === "ghost"
      ? { padding: "5px 10px", borderRadius: 7, border: `1px solid ${C.border}`, background: "transparent", color: C.muted, fontWeight: 600 }
      : { padding: "8px 14px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontWeight: 600 };
  return (
    <button {...p} style={{ ...base, fontSize: kind === "ghost" ? 12 : 13, cursor: "pointer", fontFamily: "inherit", ...(p.disabled ? { opacity: 0.5, cursor: "not-allowed" } : {}), ...(p.style || {}) }}>
      {children}
    </button>
  );
}

const CHIP_TONES = {
  muted: [C.card, C.muted, C.border],
  lime: [C.limeDim, C.lime, "rgba(165,206,58,.4)"],
  blue: ["rgba(59,130,246,.14)", C.blue, "rgba(59,130,246,.4)"],
  green: ["rgba(16,185,129,.14)", C.green, "rgba(16,185,129,.4)"],
  amber: ["rgba(245,158,11,.14)", C.amber, "rgba(245,158,11,.4)"],
  red: ["rgba(239,68,68,.14)", C.red, "rgba(239,68,68,.4)"],
  purple: ["rgba(139,92,246,.14)", C.purple, "rgba(139,92,246,.4)"],
};
// Conversation history: how many rows before the "All N" toggle.
const COMMS_LOG_PAGE = 8;
const COMMS_KIND_ICON = { call: "📞", text: "💬", note: "📝" };

function Chip({ tone = "muted", children }) {
  const [bg, fg, bd] = CHIP_TONES[tone] || CHIP_TONES.muted;
  return <span style={{ display: "inline-block", padding: "2px 9px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: bg, color: fg, border: `1px solid ${bd}`, whiteSpace: "nowrap" }}>{children}</span>;
}

// One tone map shared by jobs / quotes / invoices / requests so the same word
// always gets the same colour.
function statusTone(s) {
  const v = String(s || "").toLowerCase();
  if (["paid", "approved", "converted", "complete", "completed", "sent", "won", "accepted", "active", "today"].includes(v)) return "green";
  if (["overdue", "past_due", "bad_debt", "late", "failed", "action_required", "archived_lost"].includes(v)) return "red";
  if (["awaiting_response", "awaiting_payment", "partial", "open", "upcoming", "scheduled", "changes_requested", "unscheduled", "pending"].includes(v)) return "amber";
  if (["draft", "new", "pushed", "pushed_to_jobber"].includes(v)) return "blue";
  return "muted";
}

function Spinner({ label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: 40, color: C.muted, fontSize: 13 }}>
      <div style={{ width: 20, height: 20, border: `2px solid ${C.limeDim}`, borderTopColor: C.lime, borderRadius: "50%", animation: "c360spin .8s linear infinite" }} />
      <style>{`@keyframes c360spin{to{transform:rotate(360deg)}}`}</style>
      {label || "Loading…"}
    </div>
  );
}
const Empty = ({ children }) => <div style={{ color: C.faint, fontSize: 13, padding: "16px 8px", textAlign: "center" }}>{children}</div>;

const CHANNEL_ICON = { email: "✉️", sms: "💬" };

// ═══════════════════════════════════════════════════════════════════════════
// WORKFLOW VIEW — one request/job's whole chain: FlowStrip + job workspace +
// invoices + this client's comms + Jobber notes/photos.
// ═══════════════════════════════════════════════════════════════════════════
function FlowView({ anchor, data, onBack, onMessage }) {
  const navigate = useNavigate();
  const [chain, setChain] = useState(null);
  const [detail, setDetail] = useState(null); // /api/flow jobDetail payload
  const [media, setMedia] = useState(null);   // /api/takeoff jobMedia payload
  const [loading, setLoading] = useState(true);

  useEffect(() => { window.scrollTo(0, 0); }, [anchor.job, anchor.request]);

  useEffect(() => {
    let dead = false;
    setChain(null); setDetail(null); setMedia(null); setLoading(true);
    (async () => {
      const body = anchor.job ? { jobNum: anchor.job } : { requestId: anchor.request };
      const ch = await callApi("/api/flow", { action: "chain", ...body });
      if (dead) return;
      if (ch.ok) setChain(ch);
      const jobNum = anchor.job || ch?.stages?.job?.jobNum;
      if (jobNum) {
        const d = await callApi("/api/flow", { action: "jobDetail", jobNum: String(jobNum) });
        if (dead) return;
        if (d.ok) {
          setDetail(d);
          if (d.job?.jobber_id) {
            callApi("/api/takeoff", { action: "jobMedia", jobberJobId: d.job.jobber_id })
              .then((m) => { if (!dead && m.ok !== false) setMedia(m); });
          }
        }
      }
      if (!dead) setLoading(false);
    })();
    return () => { dead = true; };
  }, [anchor.job, anchor.request]);

  const req = chain?.stages?.request;
  const job = detail?.job || null;
  const jobNum = job?.job_num || anchor.job || chain?.stages?.job?.jobNum || null;

  const hoursByTech = useMemo(() => {
    const map = {};
    for (const ts of detail?.timesheets || []) {
      const name = ts.user_name || "Unknown";
      map[name] = (map[name] || 0) + (Number(ts.duration_hours) || 0);
    }
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [detail]);
  const totalHours = hoursByTech.reduce((s, [, h]) => s + h, 0);
  const expenseTotal = (detail?.expenses || []).reduce((s, e) => s + (Number(e.total) || 0), 0);

  const nativeInvoices = (data?.invoices || []).filter((iv) => iv.source === "quickbooks" && jobNum && String(iv.jobNum || "") === String(jobNum));
  const comms = (data?.comms || []).slice(0, 8);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
        <Btn kind="ghost" onClick={onBack}>‹ {data?.client?.name || "Client"} — all work</Btn>
        <div style={{ display: "flex", gap: 8 }}>
          {jobNum && <Btn kind="ghost" onClick={() => navigate(`/job/${jobNum}`)}>Open full job page →</Btn>}
          <Btn kind="ghost" onClick={() => onMessage(data.client)}>✉️ Message</Btn>
        </div>
      </div>

      <FlowStrip anchor={anchor.job ? { job: anchor.job } : { request: anchor.request }} style={{ marginBottom: 14 }} />
      {loading && <Spinner label="Loading workflow…" />}

      {/* ── Request ── */}
      {!loading && req && (
        <div style={S.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={S.sectionH}>Request</div>
            <div style={{ display: "flex", gap: 6 }}>
              <Btn kind="ghost" onClick={() => navigate(`/?request=${encodeURIComponent(req.id)}`)}>Open request workspace →</Btn>
              {req.webUri && <Btn kind="ghost" onClick={() => window.open(req.webUri, "_blank", "noopener")}>Jobber ↗</Btn>}
            </div>
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4 }}>{req.title || "(no description)"}</div>
          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            {req.status && <Chip tone={statusTone(req.status)}>{titleCase(req.status)}</Chip>}
            {req.bucket && <Chip tone={req.bucket === "active" ? "amber" : "muted"}>{titleCase(req.bucket)}</Chip>}
          </div>
        </div>
      )}
      {!loading && !req && !jobNum && (
        <div style={{ ...S.card, color: C.muted }}>Nothing further in this chain yet — the strip above shows what exists.</div>
      )}

      {/* ── Job workspace ── */}
      {!loading && job && (
        <div style={S.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 22, letterSpacing: ".04em", color: C.lime }}>Job #{job.job_num}</span>
              <span style={{ fontSize: 13.5, fontWeight: 600 }}>{job.title || ""}</span>
              <Chip tone={statusTone(job.status)}>{titleCase(job.status)}</Chip>
              <Chip tone={statusTone(job.derived_status || job.invoice_status)}>{titleCase(job.derived_status || job.invoice_status || "no invoice")}</Chip>
            </div>
            {job.webUri && <Btn kind="ghost" onClick={() => window.open(job.webUri, "_blank", "noopener")}>Jobber ↗</Btn>}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 8, marginBottom: 12 }}>
            {[
              ["Revenue", money(job.total_revenue), C.text],
              ["Invoiced", money(job.amount_invoiced), C.text],
              ["Outstanding", money(job.amount_outstanding), job.amount_outstanding > 0 ? C.amber : C.text],
              ["Expenses", (detail?.expenses || []).length ? money(expenseTotal) : "—", C.text],
              ["Hours", totalHours ? totalHours.toFixed(1) : "—", C.text],
            ].map(([label, val, color]) => (
              <div key={label} style={{ background: C.card2, borderRadius: 8, padding: "8px 12px" }}>
                <div style={{ fontSize: 10, letterSpacing: ".07em", textTransform: "uppercase", color: C.muted, fontWeight: 700 }}>{label}</div>
                <div style={{ fontSize: 16, fontWeight: 700, color }}>{val}</div>
              </div>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 14 }}>
            <div>
              <div style={S.sectionH}>Visits</div>
              {!(detail.visits || []).length ? <Empty>No visits synced.</Empty> : detail.visits.map((v) => (
                <div key={v.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: `1px solid ${C.card2}`, fontSize: 12.5 }}>
                  <span style={{ color: v.is_complete ? C.green : C.muted, width: 14, textAlign: "center" }}>{v.is_complete ? "✓" : "○"}</span>
                  <span style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{fmtDateShort(v.start_at)} <span style={{ color: C.muted, fontWeight: 400 }}>{fmtTime(v.start_at)}</span></span>
                  <span style={{ flex: 1, minWidth: 80, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v.title || "—"}</span>
                  <span style={{ color: C.faint, fontSize: 11.5, whiteSpace: "nowrap", maxWidth: "38%", overflow: "hidden", textOverflow: "ellipsis" }}>{(v.assigned_users || []).map((u) => u.name).filter(Boolean).join(", ")}</span>
                </div>
              ))}
              {hoursByTech.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 12, color: C.muted }}>
                  <b style={{ color: C.text }}>Hours:</b> {hoursByTech.map(([n, h]) => `${n} ${h.toFixed(1)}h`).join(" · ")}
                </div>
              )}
            </div>

            <div>
              <div style={S.sectionH}>Supplier expenses</div>
              {!(detail.expenses || []).length ? <Empty>None linked to this job.</Empty> : detail.expenses.slice(0, 10).map((e) => (
                <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: `1px solid ${C.card2}`, fontSize: 12.5 }}>
                  <span style={{ fontWeight: 600, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.supplier || "—"}</span>
                  <span style={{ color: C.faint, fontSize: 11.5 }}>{fmtDateShort(e.invoice_date || e.created_at)}</span>
                  <span style={{ fontWeight: 700, whiteSpace: "nowrap" }}>{money(e.total)}</span>
                  <Chip tone={e.status === "pushed" ? "green" : "amber"}>{e.status}</Chip>
                </div>
              ))}
              {(detail.expenses || []).length > 10 && <div style={{ fontSize: 11.5, color: C.faint, marginTop: 4 }}>+{detail.expenses.length - 10} more on the full job page</div>}
            </div>
          </div>
        </div>
      )}

      {/* ── Invoices (live Jobber + native QuickBooks rows) ── */}
      {!loading && (job || nativeInvoices.length > 0) && (
        <div style={S.card}>
          <div style={S.sectionH}>Invoices</div>
          {detail?.invoices == null && job && (
            <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 8 }}>
              Live Jobber list unavailable — rollup: invoiced <b style={{ color: C.text }}>{money(job.amount_invoiced)}</b>,
              outstanding <b style={{ color: job.amount_outstanding > 0 ? C.amber : C.text }}>{money(job.amount_outstanding)}</b>.
            </div>
          )}
          {(detail?.invoices || []).map((inv) => (
            <div key={inv.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0", borderBottom: `1px solid ${C.card2}`, fontSize: 13, flexWrap: "wrap" }}>
              <span style={{ fontWeight: 700, minWidth: 60 }}>#{inv.number}</span>
              <Chip tone={statusTone(inv.status)}>{titleCase(inv.status)}</Chip>
              <span style={{ color: C.muted, fontSize: 12 }}>{fmtDate(inv.issuedDate)}</span>
              <span style={{ fontWeight: 600 }}>{money(inv.total)}</span>
              <span style={{ color: inv.balance > 0 ? C.amber : C.muted, fontSize: 12 }}>{inv.balance > 0 ? `${money(inv.balance)} owing` : "paid in full"}</span>
              {inv.webUri && <a href={inv.webUri} target="_blank" rel="noreferrer" style={{ color: "#7dd3fc", fontSize: 12, textDecoration: "none", marginLeft: "auto" }}>Jobber ↗</a>}
            </div>
          ))}
          {Array.isArray(detail?.invoices) && detail.invoices.length === 0 && nativeInvoices.length === 0 && (
            <Empty>No invoices on this job yet.</Empty>
          )}
          {nativeInvoices.map((iv) => (
            <div key={iv.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0", borderBottom: `1px solid ${C.card2}`, fontSize: 13, flexWrap: "wrap" }}>
              <span style={{ fontWeight: 700, minWidth: 60 }}>#{iv.number || "—"}</span>
              <Chip tone="purple">QuickBooks</Chip>
              <Chip tone={statusTone(iv.status)}>{titleCase(iv.status)}</Chip>
              <span style={{ color: C.muted, fontSize: 12 }}>{fmtDate(iv.issuedDate)}</span>
              <span style={{ fontWeight: 600 }}>{money(iv.total)}</span>
              <span style={{ color: iv.balance > 0 ? C.amber : C.muted, fontSize: 12 }}>
                {iv.balance > 0 ? `${money(iv.balance)} owing${iv.daysOverdue > 0 ? ` · ${iv.daysOverdue}d overdue` : ""}` : "paid"}
              </span>
              {iv.reminderCount > 0 && <span style={{ color: C.faint, fontSize: 11.5 }}>{iv.reminderCount} reminder{iv.reminderCount > 1 ? "s" : ""} sent</span>}
            </div>
          ))}
        </div>
      )}

      {/* ── Communications (this client) ── */}
      {!loading && (
        <div style={S.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={S.sectionH}>Communications — {data?.client?.name || "client"}</div>
            <Btn kind="ghost" onClick={() => onMessage(data.client)}>✉️ New message</Btn>
          </div>
          {!comms.length ? <Empty>No ElecBid messages logged for this client yet.</Empty> : comms.map((cm) => (
            <div key={cm.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: `1px solid ${C.card2}`, fontSize: 12.5 }}>
              <span>{CHANNEL_ICON[cm.channel] || "•"}</span>
              <Chip tone={statusTone(cm.status)}>{titleCase(cm.status)}</Chip>
              <span style={{ flex: 1, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {cm.subject ? `${cm.subject} — ` : ""}{cm.preview}{cm.isTest ? " (test)" : ""}
              </span>
              <span style={{ color: C.faint, fontSize: 11.5, whiteSpace: "nowrap" }}>{fmtDateTime(cm.when)}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Notes & photos — media from our archive (/api/job-media); note text
             still rides the live jobMedia path. Renders for request-anchored
             chains too (assessment photos exist before any job does). ── */}
      {!loading && (job || anchor.request) && (
        <div style={S.card}>
          <div style={S.sectionH}>Notes &amp; photos</div>
          <div style={{ marginBottom: 10 }}>
            <MediaGallery
              scope={jobNum ? { job: jobNum } : { request: anchor.request }}
              thumbSize={72}
              emptyText={job ? null : "No photos on this request yet."}
            />
          </div>
          {job && !media && <div style={{ color: C.muted, fontSize: 13 }}>Loading notes from Jobber…</div>}
          {media && !(media.notes || []).length && (
            <Empty>{media.reason || "No Jobber notes on this job."}</Empty>
          )}
          {(media?.notes || []).map((n, i) => (
            <div key={i} style={{ padding: "7px 0", borderBottom: `1px solid ${C.card2}` }}>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>
                {/* jobMedia now surfaces the whole workflow union — job/request/quote/client */}
                <span style={{ textTransform: "capitalize" }}>{n.source || "job"}</span> note
                {n.author ? ` · ${n.author}` : ""} · {fmtDate(n.createdAt)}
              </div>
              <div style={{ fontSize: 13, whiteSpace: "pre-wrap", lineHeight: 1.45 }}>{n.message}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CLIENT 360
// ═══════════════════════════════════════════════════════════════════════════
export default function Client360({ clientId, onMessage }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [showPastVisits, setShowPastVisits] = useState(false);
  const [showAllComms, setShowAllComms] = useState(false);

  const anchorJob = params.get("job");
  const anchorRequest = params.get("request");

  useEffect(() => {
    let dead = false;
    setData(null); setErr(null);
    (async () => {
      const r = await callApi("/api/pipeline?action=client-360", { action: "client-360", clientId });
      if (dead) return;
      if (r.ok) setData(r); else setErr(r.error || "Failed to load client");
    })();
    return () => { dead = true; };
  }, [clientId]);

  const setDrill = (mut) => { const n = new URLSearchParams(params); mut(n); setParams(n); };
  const openJob = (jobNum) => setDrill((n) => { n.delete("request"); n.set("job", String(jobNum)); });
  const openRequest = (id) => setDrill((n) => { n.delete("job"); n.set("request", id); });
  const backTo360 = () => setDrill((n) => { n.delete("job"); n.delete("request"); });
  const backToList = () => setDrill((n) => { n.delete("client"); n.delete("job"); n.delete("request"); });

  if (err) return (
    <div>
      <Btn kind="ghost" onClick={backToList}>‹ All clients</Btn>
      <div style={{ ...S.card, marginTop: 12, color: C.red }}>Couldn't load client: {err}</div>
    </div>
  );
  if (!data) return <Spinner label="Loading client…" />;

  if (anchorJob || anchorRequest) {
    return (
      <FlowView
        anchor={anchorJob ? { job: anchorJob } : { request: anchorRequest }}
        data={data}
        onBack={backTo360}
        onMessage={onMessage}
      />
    );
  }

  const { client, summary } = data;
  const upcoming = data.visits.filter((v) => !v.complete && new Date(v.startAt).getTime() >= Date.now()).sort((a, b) => new Date(a.startAt) - new Date(b.startAt));
  const past = data.visits.filter((v) => !upcoming.includes(v));
  const quotes = [
    ...data.quotes.smart.map((q) => ({ ...q, kind: "smart", when: q.createdAt })),
    ...data.quotes.jobber.map((q) => ({ ...q, kind: "jobber", when: q.sentDate })),
  ].sort((a, b) => new Date(b.when || 0) - new Date(a.when || 0));

  const tiles = [
    ["Requests", `${summary.openRequests} open / ${summary.requests}`, C.amber],
    ["Quotes", summary.quotes, C.blue],
    ["Jobs", `${summary.openJobs} open / ${summary.jobs}`, C.lime],
    ["Invoiced (tax-in)", money(summary.invoicedTaxIn), C.text],
    ["Outstanding", money(summary.outstanding), summary.outstanding > 0 ? C.red : C.text],
    ["Upcoming visits", summary.upcomingVisits, C.purple],
  ];

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <div style={{ minWidth: 220 }}>
          <Btn kind="ghost" onClick={backToList} style={{ marginBottom: 8 }}>‹ All clients</Btn>
          <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 28, letterSpacing: ".03em", color: C.text, lineHeight: 1.1 }}>{client.name}</div>
          <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
            {client.phone && <Chip tone="green">{client.phone}</Chip>}
            {client.email && <Chip tone="blue">{client.email}</Chip>}
          </div>
          {client.address && <div style={{ fontSize: 12.5, color: C.muted, marginTop: 6 }}>{client.address}</div>}
          {summary.lastActivity && <div style={{ fontSize: 11.5, color: C.faint, marginTop: 4 }}>Last activity {fmtDate(summary.lastActivity)}</div>}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Btn kind="pri" onClick={() => onMessage(client)}>✉️ Message</Btn>
          {client.webUri && <Btn onClick={() => window.open(client.webUri, "_blank", "noopener")}>Open in Jobber ↗</Btn>}
        </div>
      </div>

      {/* ── Tiles ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 10, marginBottom: 14 }}>
        {tiles.map(([label, val, color]) => (
          <div key={label} style={{ ...S.card, margin: 0, padding: "12px 14px" }}>
            <div style={{ fontSize: 20, fontWeight: 800, color, lineHeight: 1.15 }}>{val}</div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>{label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(340px,1fr))", gap: 14 }}>
        {/* ── Requests ── */}
        <div style={S.card}>
          <div style={S.sectionH}>Requests</div>
          {!data.requests.length ? <Empty>No requests on file.</Empty> : data.requests.map((r) => (
            <div key={r.id} onClick={() => openRequest(r.id)} style={{ ...S.row, cursor: "pointer" }}
              onMouseEnter={(e) => { e.currentTarget.style.outline = `1px solid rgba(165,206,58,.4)`; }}
              onMouseLeave={(e) => { e.currentTarget.style.outline = "none"; }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.title || titleCase(r.status) || "Request"}</div>
                <div style={{ fontSize: 11.5, color: C.faint }}>
                  {fmtDate(r.createdAt)}{r.jobCount ? ` · ${r.jobCount} job${r.jobCount > 1 ? "s" : ""}` : ""}{r.quoteCount ? ` · ${r.quoteCount} quote${r.quoteCount > 1 ? "s" : ""}` : ""}
                </div>
              </div>
              <Chip tone={r.bucket === "active" ? "amber" : "muted"}>{titleCase(r.bucket)}</Chip>
            </div>
          ))}
        </div>

        {/* ── Quotes ── */}
        <div style={S.card}>
          <div style={S.sectionH}>Quotes</div>
          {!quotes.length ? <Empty>No quotes on file.</Empty> : quotes.map((q) => (
            <div key={q.kind === "smart" ? `s${q.id}` : `j${q.number}`}
              onClick={q.kind === "smart" ? () => navigate(`/quote/${q.id}`) : undefined}
              style={{ ...S.row, cursor: q.kind === "smart" ? "pointer" : "default" }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {q.kind === "smart"
                    ? (q.title || titleCase(q.serviceType) || "ElecBid quote")
                    : `Jobber quote #${q.number}`}
                  {q.kind === "smart" && q.jobberNumber ? <span style={{ color: C.faint, fontWeight: 400 }}> → #{q.jobberNumber}</span> : null}
                </div>
                <div style={{ fontSize: 11.5, color: C.faint }}>{fmtDate(q.when)}{q.kind === "smart" ? " · ElecBid" : ""}</div>
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: C.lime, whiteSpace: "nowrap" }}>{money(q.total)}</span>
                <Chip tone={statusTone(q.state || q.status)}>{titleCase(q.state || q.status || "—")}</Chip>
              </div>
            </div>
          ))}
        </div>

        {/* ── Jobs ── */}
        <div style={S.card}>
          <div style={S.sectionH}>Jobs</div>
          {!data.jobs.length ? <Empty>No jobs on file.</Empty> : data.jobs.map((j) => (
            <div key={j.jobNum} onClick={() => openJob(j.jobNum)} style={{ ...S.row, cursor: "pointer" }}
              onMouseEnter={(e) => { e.currentTarget.style.outline = `1px solid rgba(165,206,58,.4)`; }}
              onMouseLeave={(e) => { e.currentTarget.style.outline = "none"; }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  #{j.jobNum} {j.title || ""}
                </div>
                <div style={{ fontSize: 11.5, color: C.faint }}>
                  {fmtDate(j.createdAt)}{j.invoiced ? ` · invoiced ${money(j.invoiced)}` : ""}{j.outstanding > 0 ? ` · ${money(j.outstanding)} owing` : ""}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <Chip tone={statusTone(j.status)}>{titleCase(j.status)}</Chip>
              </div>
            </div>
          ))}
        </div>

        {/* ── Visits ── */}
        <div style={S.card}>
          <div style={S.sectionH}>Visits</div>
          {!upcoming.length && !past.length && <Empty>No visits on file.</Empty>}
          {upcoming.map((v) => (
            <div key={v.id} style={S.row}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v.title || (v.jobNumber ? `Job #${v.jobNumber}` : "Visit")}</div>
                <div style={{ fontSize: 11.5, color: C.faint }}>{v.assigned.join(", ") || "unassigned"}</div>
              </div>
              <span style={{ fontSize: 12, color: C.lime, fontWeight: 700, whiteSpace: "nowrap" }}>{fmtDateTime(v.startAt)}</span>
            </div>
          ))}
          {past.length > 0 && (
            <div style={{ marginTop: 4 }}>
              <Btn kind="ghost" onClick={() => setShowPastVisits(!showPastVisits)}>
                {showPastVisits ? "Hide" : "Show"} {past.length} past visit{past.length > 1 ? "s" : ""}
              </Btn>
              {showPastVisits && past.slice(0, 25).map((v) => (
                <div key={v.id} style={{ ...S.row, opacity: 0.75, marginTop: 6 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      <span style={{ color: v.complete ? C.green : C.muted }}>{v.complete ? "✓ " : "○ "}</span>
                      {v.title || (v.jobNumber ? `Job #${v.jobNumber}` : "Visit")}
                    </div>
                    <div style={{ fontSize: 11, color: C.faint }}>{v.assigned.join(", ")}</div>
                  </div>
                  <span style={{ fontSize: 11.5, color: C.muted, whiteSpace: "nowrap" }}>{fmtDateShort(v.startAt)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Invoices ── */}
        <div style={S.card}>
          <div style={S.sectionH}>Invoices</div>
          {!data.invoices.length ? <Empty>No invoices on file.</Empty> : data.invoices.map((iv) => (
            <div key={iv.id}
              onClick={iv.jobNum ? () => openJob(iv.jobNum) : undefined}
              style={{ ...S.row, cursor: iv.jobNum ? "pointer" : "default" }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap" }}>
                  #{iv.number || "—"}{iv.jobNum ? <span style={{ color: C.faint, fontWeight: 400 }}> · job {iv.jobNum}</span> : null}
                </div>
                <div style={{ fontSize: 11.5, color: C.faint }}>
                  {fmtDate(iv.issuedDate)} · {iv.source === "quickbooks" ? "QuickBooks" : "Jobber"}
                  {iv.reminderCount > 0 ? ` · ${iv.reminderCount} reminder${iv.reminderCount > 1 ? "s" : ""}` : ""}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: iv.balance > 0 ? C.red : C.text, whiteSpace: "nowrap" }}>
                  {iv.balance > 0 ? money(iv.balance) : money(iv.total)}
                </span>
                <Chip tone={statusTone(iv.status)}>{titleCase(iv.status)}</Chip>
              </div>
            </div>
          ))}
        </div>

        {/* ── Conversation history (roadmap #4b) ──
            client_comm_notes + call_transcripts have captured every call and text
            natively all along, but nothing rendered them — so Jobber's note
            timeline was the only place a human could read a client's history.
            This makes the Jobber notes a mirror rather than the record. */}
        <div style={S.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={S.sectionH}>Conversation history</div>
            {(data.commsLog || []).length > COMMS_LOG_PAGE && (
              <Btn kind="ghost" onClick={() => setShowAllComms((v) => !v)}>
                {showAllComms ? "Show less" : `All ${data.commsLog.length}`}
              </Btn>
            )}
          </div>
          {!(data.commsLog || []).length ? (
            <Empty>No calls or texts logged for this client yet.</Empty>
          ) : (
            (showAllComms ? data.commsLog : data.commsLog.slice(0, COMMS_LOG_PAGE)).map((e) => (
              <div key={e.key} style={{ display: "flex", gap: 9, padding: "8px 0", borderBottom: `1px solid ${C.card2}`, fontSize: 12.5, alignItems: "flex-start" }}>
                <span title={e.kind} style={{ lineHeight: "18px" }}>{COMMS_KIND_ICON[e.kind] || "•"}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: C.text, wordBreak: "break-word" }}>{e.text}</div>
                  <div style={{ color: C.faint, fontSize: 11, marginTop: 2 }}>
                    {[
                      e.verb,
                      e.durationSeconds ? `${Math.round(e.durationSeconds / 60)} min` : null,
                      e.via,
                    ].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <span style={{ color: C.faint, fontSize: 11.5, whiteSpace: "nowrap" }}>
                  {e.at ? fmtDateShort(e.at) : (e.dateLabel || "")}
                </span>
              </div>
            ))
          )}
        </div>

        {/* ── Communications ── */}
        <div style={S.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={S.sectionH}>Messages sent by ElecBid</div>
            <Btn kind="ghost" onClick={() => onMessage(client)}>✉️ New</Btn>
          </div>
          {!data.comms.length ? <Empty>No ElecBid messages logged yet.</Empty> : data.comms.map((cm) => (
            <div key={cm.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: `1px solid ${C.card2}`, fontSize: 12.5 }}>
              <span>{CHANNEL_ICON[cm.channel] || "•"}</span>
              <Chip tone={statusTone(cm.status)}>{titleCase(cm.status)}</Chip>
              <span style={{ flex: 1, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {cm.subject ? `${cm.subject} — ` : ""}{cm.preview}{cm.isTest ? " (test)" : ""}
              </span>
              <span style={{ color: C.faint, fontSize: 11.5, whiteSpace: "nowrap" }}>{fmtDateShort(cm.when)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Photos — the whole relationship, from our re-hosted archive.
          Full-width below the grid; thumbnails badge the job they belong to. ── */}
      <div style={{ ...S.card, marginTop: 14 }}>
        <div style={S.sectionH}>Photos &amp; files</div>
        <MediaGallery scope={{ client: clientId }} showContext initial={36} emptyText="No photos on file for this client." />
      </div>
    </div>
  );
}
